import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Package, Clock, CheckCircle, XCircle, Truck, RefreshCw, MapPin, DollarSign, Home, LogOut, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AppFooter from '@/components/AppFooter';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparing: { label: 'Preparing', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-purple-100 text-purple-800', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const Orders = () => {
  const { logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders(selectedTab);
  }, [selectedTab]);

  const fetchOrders = async (status = 'all') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/orders`, {
        params: { status: status === 'all' ? null : status, limit: 20 },
        withCredentials: true
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const viewOrderDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/api/orders/${orderId}`, {
        withCredentials: true
      });
      setSelectedOrder(response.data.order);
      setShowDetailDialog(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to load order details');
    }
  };

  const handleReorder = async (orderId) => {
    try {
      const response = await axios.post(`${API}/api/orders/${orderId}/reorder`, {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success('Order placed successfully!');
        fetchOrders(selectedTab);
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reorder');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;

    try {
      const response = await axios.put(`${API}/api/orders/${orderId}/cancel`, {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success('Order cancelled successfully');
        fetchOrders(selectedTab);
        setShowDetailDialog(false);
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getActiveOrders = () => orders.filter(o => ['confirmed', 'preparing', 'out_for_delivery'].includes(o.status));
  const getCompletedOrders = () => orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  const OrderCard = ({ order }) => {
    const config = statusConfig[order.status];
    const StatusIcon = config.icon;

    return (
      <Card className="mb-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewOrderDetails(order.id)}>
        <CardContent className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-lg">{order.vendor_name}</h3>
                <Badge className={config.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {config.label}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">Order #{order.order_number}</p>
              <p className="text-sm text-gray-500">{formatDate(order.ordered_at)} at {formatTime(order.ordered_at)}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-indigo-600">${order.total.toFixed(2)}</p>
              <p className="text-sm text-gray-500">{order.items.length} item{order.items.length > 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {order.items.slice(0, 2).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-700">{item.quantity}x {item.meal_name}</span>
                <span className="text-gray-600">${item.subtotal.toFixed(2)}</span>
              </div>
            ))}
            {order.items.length > 2 && (
              <p className="text-sm text-gray-500">+ {order.items.length - 2} more item{order.items.length - 2 > 1 ? 's' : ''}</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                viewOrderDetails(order.id);
              }}
            >
              View Details
            </Button>
            {order.status === 'delivered' && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReorder(order.id);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Reorder
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const OrderDetailDialog = () => {
    if (!selectedOrder) return null;

    const config = statusConfig[selectedOrder.status];
    const StatusIcon = config.icon;
    const estimatedDelivery = new Date(selectedOrder.estimated_delivery_time);

    return (
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription>Order #{selectedOrder.order_number}</DialogDescription>
          </DialogHeader>

          {/* Status Progress */}
          <div className="my-6">
            <div className="flex items-center justify-between mb-4">
              <Badge className={`${config.color} text-sm px-3 py-1`}>
                <StatusIcon className="w-4 h-4 mr-1" />
                {config.label}
              </Badge>
              <p className="text-sm text-gray-600">
                {selectedOrder.status === 'delivered' 
                  ? `Delivered ${formatDate(selectedOrder.delivered_at)}`
                  : `Est. delivery: ${formatTime(selectedOrder.estimated_delivery_time)}`
                }
              </p>
            </div>

            {/* Progress Bar */}
            <div className="relative">
              <div className="h-2 bg-gray-200 rounded-full">
                <div 
                  className={`h-2 rounded-full transition-all ${
                    selectedOrder.status === 'delivered' ? 'bg-green-500' : 
                    selectedOrder.status === 'cancelled' ? 'bg-red-500' : 'bg-indigo-500'
                  }`}
                  style={{
                    width: selectedOrder.status === 'confirmed' ? '25%' : 
                           selectedOrder.status === 'preparing' ? '50%' :
                           selectedOrder.status === 'out_for_delivery' ? '75%' : '100%'
                  }}
                />
              </div>
            </div>

            {/* Status History */}
            <div className="mt-4 space-y-2">
              {selectedOrder.status_history?.map((history, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="capitalize">{history.status.replace('_', ' ')}</span>
                  <span className="text-gray-400">• {formatTime(history.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Vendor Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vendor</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold">{selectedOrder.vendor_name}</p>
            </CardContent>
          </Card>

          {/* Order Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Order Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedOrder.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <div>
                      <p className="font-medium">{item.meal_name}</p>
                      <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                    </div>
                    <p className="font-semibold">${item.subtotal.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Delivery Address
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>{selectedOrder.delivery_address.street}</p>
              {selectedOrder.delivery_address.apartment && (
                <p>{selectedOrder.delivery_address.apartment}</p>
              )}
              <p>{selectedOrder.delivery_address.city}, {selectedOrder.delivery_address.state} {selectedOrder.delivery_address.zip_code}</p>
              {selectedOrder.delivery_address.delivery_instructions && (
                <p className="text-sm text-gray-600 mt-2">
                  <em>Instructions: {selectedOrder.delivery_address.delivery_instructions}</em>
                </p>
              )}
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Payment Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${selectedOrder.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Delivery Fee</span>
                  <span>${selectedOrder.delivery_fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax</span>
                  <span>${selectedOrder.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span className="text-indigo-600">${selectedOrder.total.toFixed(2)}</span>
                </div>
                <p className="text-sm text-gray-600 capitalize">
                  Payment: {selectedOrder.payment_method.replace('_', ' ')} ({selectedOrder.payment_status})
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            {selectedOrder.can_cancel && selectedOrder.status === 'confirmed' && (
              <Button
                variant="destructive"
                onClick={() => handleCancelOrder(selectedOrder.id)}
              >
                Cancel Order
              </Button>
            )}
            {selectedOrder.status === 'delivered' && (
              <Button
                onClick={() => {
                  handleReorder(selectedOrder.id);
                  setShowDetailDialog(false);
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reorder
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 md:top-16 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/dashboard" className="text-xl font-bold text-indigo-600">HealthLoop</Link>
              <div className="flex items-center gap-4">
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" />Dashboard</Button>
                </Link>
                <Link to="/profile">
                  <Button variant="ghost" size="sm"><User className="w-4 h-4 mr-2" />Profile</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
              </div>
            </div>
          </div>
        </nav>
        
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 md:top-16 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="text-xl font-bold text-indigo-600">HealthLoop</Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" />Dashboard</Button>
              </Link>
              <Link to="/nutrition">
                <Button variant="ghost" size="sm">Browse Meals</Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm"><User className="w-4 h-4 mr-2" />Profile</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
          <p className="text-gray-600">Track your meal orders and reorder favorites</p>
        </div>

        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all">
              All Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="active">
              Active ({getActiveOrders().length})
            </TabsTrigger>
            <TabsTrigger value="delivered">
              Completed ({getCompletedOrders().length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {orders.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No orders yet</h3>
                  <p className="text-gray-600 mb-4">Start ordering healthy meals from local vendors!</p>
                  <Button onClick={() => navigate('/nutrition')}>
                    Browse Meals
                  </Button>
                </CardContent>
              </Card>
            ) : (
              orders.map(order => <OrderCard key={order.id} order={order} />)
            )}
          </TabsContent>

          <TabsContent value="active">
            {getActiveOrders().length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No active orders</h3>
                  <p className="text-gray-600">You don't have any orders in progress</p>
                </CardContent>
              </Card>
            ) : (
              getActiveOrders().map(order => <OrderCard key={order.id} order={order} />)
            )}
          </TabsContent>

          <TabsContent value="delivered">
            {getCompletedOrders().length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No completed orders</h3>
                  <p className="text-gray-600">Your delivered orders will appear here</p>
                </CardContent>
              </Card>
            ) : (
              getCompletedOrders().map(order => <OrderCard key={order.id} order={order} />)
            )}
          </TabsContent>
        </Tabs>

        {selectedOrder && <OrderDetailDialog />}
      </div>
      
      <AppFooter />
    </div>
  );
};

export default Orders;
